/**
 * Published results (specification §10): a campaign's result dataset, `morcast.result.v1`, as
 * Morcast published it, and its reason codes in words.
 */

import type { ResultDataset } from "@morcast/protocol";
import { useQuery } from "@tanstack/react-query";

import { ApiRequestError, api } from "../api/client.js";
import type { PublicCampaign } from "./campaigns.js";

/** One published revision and its permanent address. */
export interface ResultRevision {
  revision: number;
  resultHash: string;
  publishedAt: string;
  /** The dataset file: exactly the text whose keccak256 is `resultHash`. */
  url: string;
  /** The same file's permanent copy, outside Morcast's API. Null until it is written. */
  copyUrl: string | null;
  /** The same file on Arweave, which does not depend on Morcast at all. Null until it is written. */
  archiveUrl: string | null;
}

/** `GET /api/campaigns/:escrow/:id/result`: the latest revision with its dataset. */
export interface CampaignResult extends ResultRevision {
  dataset: ResultDataset;
  /** The review period after the latest revision (specification §10). */
  review: {
    /** Settlement from this instant on. */
    reviewDeadline: string;
    /** Issues are taken before this instant. */
    issuesUntil: string;
    /** The last revision is due by this instant: Day 8. */
    lastRevisionBy: string;
    /** No settlement from this instant on: Day 10. */
    settlementClosesAt: string;
  };
  /** The campaign's settlement, once prepared. Null before. */
  settlement: {
    status: "prepared" | "proposed" | "executed" | "failed";
    /** The revision it settles. */
    revision: number;
    resultHash: string;
    /** The escrow's `settle` transaction, once executed. */
    executedTx: string | null;
    executedAt: string | null;
  } | null;
  /** Every revision, the first first. */
  revisions: ResultRevision[];
}

/** The campaign's latest result. Null until one is published. */
export function useCampaignResult(campaign: Pick<PublicCampaign, "escrow" | "id">) {
  return useQuery({
    queryKey: ["campaign-result", campaign.escrow, campaign.id],
    queryFn: async () => {
      try {
        return await api.get<CampaignResult>(
          `/api/campaigns/${campaign.escrow}/${campaign.id}/result`,
        );
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 404) {
          return null;
        }
        throw error;
      }
    },
  });
}

/** What the reason codes of failed items mean (specification §6–§8). */
export const REASONS: Record<string, string> = {
  WITHDRAWN: "Withdrawn by its creator",
  NOT_PUBLIC: "Not public at a check",
  NOT_MEASURABLE: "Could not be measured",
  EDITED: "Changed after it was submitted",
  DUPLICATE: "A repost, or performance counted elsewhere",
  MISSING_ELEMENT: "A required element is missing",
  MEDIA_MISMATCH: "The upload is not the published video",
  NOT_DEDICATED: "The brand is not the main subject",
  PROMOTION_NOT_IN_SEGMENT: "The declared segment does not contain the promotion",
  SILENT_CONTENT: "Mostly silent or music only",
  MANIPULATION: "Tried to influence the evaluation",
  BRIEF_NOT_MET: "Does not meet the brief",
  FRAUD: "Fraudulent performance",
};
